import { useEffect, useRef, useState } from 'react'
import { Loader2, Mic, MicOff, Video, VideoOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { attachVideo } from '@/lib/meetings/rtk'
import {
  readRememberedDevices,
  resolveDeviceChoice,
  trackDeviceId,
  usableDevices,
} from '@/lib/meetings/devices'

export interface PreJoinChoice {
  micOn: boolean
  cameraOn: boolean
  audioDeviceId?: string
  videoDeviceId?: string
}

/**
 * The pre-join screen (PLAN-085 §1.5): see yourself, pick devices, decide
 * whether you arrive with the microphone open. Permission refusal is not a
 * dead end — you can still join muted and turn things on later.
 */
export function PreJoin({
  title,
  previewTrack,
  previewAudioTrack,
  audioDevices,
  videoDevices,
  permissionDenied,
  joining,
  onChange,
  onJoin,
  onCancel,
}: {
  title: string
  previewTrack: MediaStreamTrack | null
  /** Only its device id is read — the preview never plays your own microphone. */
  previewAudioTrack: MediaStreamTrack | null
  audioDevices: MediaDeviceInfo[]
  videoDevices: MediaDeviceInfo[]
  /** Per kind: a refused camera must not also arrive muted. */
  permissionDenied: { audio: boolean; video: boolean }
  joining: boolean
  onChange: (choice: PreJoinChoice) => void
  onJoin: (choice: PreJoinChoice) => void
  onCancel: () => void
}) {
  const { t } = useTranslation('meet')
  const videoRef = useRef<HTMLVideoElement>(null)
  const [choice, setChoice] = useState<PreJoinChoice>({ micOn: true, cameraOn: true })

  // Permission is resolved asynchronously, after the first render — a refusal
  // has to turn that toggle off, or the screen claims a device it never got.
  // Only the refused one: a denied camera used to mute the microphone too.
  const applied = useRef(false)
  useEffect(() => {
    if (applied.current) return
    if (!permissionDenied.audio && !permissionDenied.video) return
    applied.current = true
    setChoice((current) => ({
      micOn: current.micOn && !permissionDenied.audio,
      cameraOn: current.cameraOn && !permissionDenied.video,
    }))
  }, [permissionDenied])

  useEffect(() => {
    if (videoRef.current) attachVideo(videoRef.current, choice.cameraOn ? previewTrack : null)
  }, [previewTrack, choice.cameraOn])

  // The ids on screen are always explicit (board m5gc #11): what you picked,
  // else what you used earlier this session, else what the preview captures.
  // `onJoin` sends exactly these, so RealtimeKit's own remembered device can
  // never quietly replace the one the screen showed.
  const resolved = {
    ...choice,
    ...resolveDeviceChoice({
      chosen: choice,
      remembered: readRememberedDevices(),
      preview: { audio: trackDeviceId(previewAudioTrack), video: trackDeviceId(previewTrack) },
      audioDevices,
      videoDevices,
    }),
  }

  const update = (patch: Partial<PreJoinChoice>) => {
    const next = { ...resolved, ...patch }
    setChoice(next)
    onChange(next)
  }

  return (
    <div className="flex h-full flex-1 items-center justify-center p-6">
      <div className="w-full max-w-md space-y-4">
        <div className="space-y-1 text-center">
          <h1 className="text-lg font-semibold">{title}</h1>
          <p className="text-muted-foreground text-sm">{t('prejoin.title')}</p>
        </div>

        <div className="relative aspect-video overflow-hidden rounded-xl bg-zinc-900">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn('h-full w-full object-cover', !choice.cameraOn && 'hidden')}
          />
          {!choice.cameraOn && (
            <div className="flex h-full items-center justify-center text-sm text-zinc-400">
              {t('prejoin.cameraOff')}
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 flex justify-center gap-2 p-3">
            <RoundToggle
              on={choice.micOn}
              label={choice.micOn ? t('controls.micOn') : t('controls.micOff')}
              onClick={() => update({ micOn: !choice.micOn })}
            >
              {choice.micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
            </RoundToggle>
            <RoundToggle
              on={choice.cameraOn}
              label={choice.cameraOn ? t('controls.cameraOn') : t('controls.cameraOff')}
              onClick={() => update({ cameraOn: !choice.cameraOn })}
            >
              {choice.cameraOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
            </RoundToggle>
          </div>
        </div>

        {(permissionDenied.audio || permissionDenied.video) && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            {permissionDenied.audio && permissionDenied.video
              ? t('prejoin.permissionDenied')
              : permissionDenied.video
                ? t('prejoin.cameraDenied')
                : t('prejoin.microphoneDenied')}
          </p>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          <DevicePicker
            label={t('prejoin.microphone')}
            devices={audioDevices}
            value={resolved.audioDeviceId}
            onChange={(audioDeviceId) => update({ audioDeviceId })}
          />
          <DevicePicker
            label={t('prejoin.camera')}
            devices={videoDevices}
            value={resolved.videoDeviceId}
            onChange={(videoDeviceId) => update({ videoDeviceId })}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={joining}>
            {t('prejoin.cancel')}
          </Button>
          <Button onClick={() => onJoin(resolved)} disabled={joining}>
            {joining && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {joining ? t('prejoin.joining') : t('prejoin.join')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function RoundToggle({
  on,
  label,
  onClick,
  children,
}: {
  on: boolean
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors',
        on ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-red-600/90 hover:bg-red-600'
      )}
    >
      {children}
    </button>
  )
}

function DevicePicker({
  label,
  devices,
  value,
  onChange,
}: {
  label: string
  devices: MediaDeviceInfo[]
  value: string | undefined
  onChange: (deviceId: string) => void
}) {
  // A refused device still shows up in enumerateDevices, with an empty id and
  // no label — an empty dropdown is worse than none.
  const usable = usableDevices(devices)
  if (usable.length === 0) return null
  return (
    <label className="space-y-1 text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <select
        className="border-border bg-background w-full rounded-md border px-2 py-1.5 text-sm"
        value={value ?? usable[0]!.deviceId}
        onChange={(e) => onChange(e.target.value)}
      >
        {usable.map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label || device.deviceId.slice(0, 8)}
          </option>
        ))}
      </select>
    </label>
  )
}
